'use strict';

var CircuitBreaker = require('opossum');
var isObject = require('is-object');

// Local cache of breaker instances
const breakers = {};

// generic response creator
const createResponse = ({
  body,
  ...rest
}) => ({
  ok: true,
  statusText: 'OK',
  status: 200,
  url: '',
  text: () => Promise.resolve(body),
  json: () => Promise.resolve(JSON.parse(body)),
  blob: () => Promise.resolve(new Blob([body])),
  ...rest
});

/**
 * Creates an instance of a circuit breaker
 * @param {object} fetcher instance of an async fetch request
 * @param {object} url Standard URL object
 * @param {object} options
 */
async function createCircuitBreaker(fetcher, url, options) {
  const {
    fallback,
    on = {},
    ...rest
  } = options;
  const breakerOptions = {
    timeout: 3000,
    // If the fetch takes longer than 3 seconds, trigger a failure
    errorThresholdPercentage: 50,
    // When 50% of requests fail, trip the circuit
    resetTimeout: 30000,
    // After 30 seconds, try again.
    errorFilter: err => err.statusCode < 500,
    // Don't increment circuit breaker if we respond quickly with an error
    ...rest
  };
  const breaker = new CircuitBreaker(fetcher, breakerOptions);
  if (fallback) {
    let response;
    if (typeof fallback === 'function') {
      const _fallback = await fallback();
      response = (href, _options, error = {}) => createResponse({
        url: href,
        ...(error.status && {
          status: error.status
        }),
        ..._fallback
      });
    }
    if (isObject(fallback)) {
      if (fallback.body && fallback.ok) {
        response = (href, _options, error = {}) => createResponse({
          url: href,
          ...(error.status && {
            status: error.status
          }),
          ...fallback
        });
      } else {
        response = (href, _options, error = {}) => createResponse({
          url: href,
          ...(error.status && {
            status: error.status
          }),
          body: JSON.stringify(fallback)
        });
      }
    }
    const onEvents = {
      fire: {},
      reject: {},
      timeout: {
        message: `request to ${url.href} is not responding. The circuit breaker for ${url.pathname} has exceeded it's ${breakerOptions.timeout} threshold`
      },
      success: {},
      failure: {},
      open: {
        message: `the circuit breaker for ${url.pathname} has opened`
      },
      close: {
        message: `the circuit breaker for ${url.pathname} has closed`
      },
      halfOpen: {
        message: `the circuit breaker for ${url.pathname} is attempting to close`
      },
      fallback: {},
      semaphoreLocked: {
        message: `the circuit breaker for ${url.pathname} is at capacity`
      },
      healthCheckFailed: {},
      shutdown: {}
    };
    Object.entries(onEvents).forEach(([onEventKey, {
      message
    }]) => {
      breaker.on(onEventKey, (...args) => {
        if (message) {
          // eslint-disable-next-line no-console
          console.log(message);
        }
        if (typeof on[onEventKey] === 'function') {
          on[onEventKey](...args);
        }
      });
    });

    // fallback accepts the same parameters as fire and in addition it provides rejected promise value(failed response or network error)
    breaker.fallback(response);
  }
  return breaker;
}
async function getCircuitBreaker(fetcher, url, options = {}) {
  const breakerId = options.id;
  if (!breakerId) {
    throw new Error(`No required option 'id' specified for circuitbreaker defined for url: \n\n ${url} \n\n Make sure you are setting options.id in your circuit breaker configuration for the bonnet fetch.`);
  }

  // Check if a circuit breaker has already been established for this path
  // and return if it has.
  if (breakers[breakerId]) {
    return breakers[breakerId];
  }

  // create new breaker
  const breaker = await createCircuitBreaker(fetcher, url, options);

  // assign the breaker to the breakers cache via pathname key
  breakers[breakerId] = breaker;
  return breaker;
}

exports.breakers = breakers;
exports.createCircuitBreaker = createCircuitBreaker;
exports.getCircuitBreaker = getCircuitBreaker;
